import React, { useContext, useEffect, useState } from "react";
import { NavLink, useLocation, useNavigate } from "react-router-dom";
import { Dispatch, SetStateAction } from "react";
import Drawer from "@mui/material/Drawer";
import { IconChevronRight, IconLogout, IconMenu2, IconX } from "@tabler/icons-react";
import { AuthContext } from "../../context/AuthContext";
import { UserContext } from "../../context/UserContext";
import Footer from "../Footer";
import { BOTTOM_NAV, NAV_ITEMS, NavEntry, isPathActive, pageTitleFor } from "./navConfig";
import mark from "../../images/square_logo.webp";
import "./SidebarLayout.scss";

interface Props {
  setLoggedIn: Dispatch<SetStateAction<boolean>>;
  children: React.ReactNode;
}

function storedUserName(): string {
  try {
    const parsed = JSON.parse(localStorage.getItem("userDetails") || "{}");
    return parsed.userName || parsed.username || "";
  } catch {
    return "";
  }
}

function NavList({
  pathname,
  onNavigate,
}: {
  pathname: string;
  onNavigate?: () => void;
}) {
  const [expanded, setExpanded] = useState<string[]>(() =>
    NAV_ITEMS.filter((i) => i.children && isPathActive(pathname, i.path)).map((i) => i.path)
  );

  // Open the group holding the current page, including after a deep link.
  useEffect(() => {
    const active = NAV_ITEMS.find((i) => i.children && isPathActive(pathname, i.path));
    if (active) {
      setExpanded((prev) => (prev.includes(active.path) ? prev : [...prev, active.path]));
    }
  }, [pathname]);

  const toggle = (path: string) =>
    setExpanded((prev) =>
      prev.includes(path) ? prev.filter((p) => p !== path) : [...prev, path]
    );

  const renderGroup = (item: NavEntry) => {
    const open = expanded.includes(item.path);
    const active = isPathActive(pathname, item.path);
    const panelId = `sq-nav-${item.path.slice(1)}`;
    return (
      <li key={item.path}>
        <button
          type="button"
          className={`sq-nav-link${active ? " is-within" : ""}`}
          aria-expanded={open}
          aria-controls={panelId}
          onClick={() => toggle(item.path)}
        >
          <item.icon size={19} stroke={1.75} />
          <span>{item.label}</span>
          <IconChevronRight size={16} className={`sq-nav-caret${open ? " is-open" : ""}`} />
        </button>
        {open ? (
          <ul className="sq-nav-sub" id={panelId}>
            {item.children!.map((child) => (
              <li key={child.path}>
                <NavLink to={child.path} className="sq-nav-link" onClick={onNavigate}>
                  <span>{child.label}</span>
                </NavLink>
              </li>
            ))}
          </ul>
        ) : null}
      </li>
    );
  };

  return (
    <ul className="sq-nav">
      {NAV_ITEMS.map((item) =>
        item.children ? (
          renderGroup(item)
        ) : (
          <li key={item.path}>
            <NavLink to={item.path} className="sq-nav-link" onClick={onNavigate}>
              <item.icon size={19} stroke={1.75} />
              <span>{item.label}</span>
            </NavLink>
          </li>
        )
      )}
    </ul>
  );
}

function UserBlock({ name, onLogout }: { name: string; onLogout: () => void }) {
  return (
    <div className="sq-user">
      <span className="sq-user-avatar" aria-hidden="true">
        {(name || "?").charAt(0).toUpperCase()}
      </span>
      <span className="sq-user-name">{name || "Signed in"}</span>
      <button type="button" className="sq-user-logout" onClick={onLogout} aria-label="Log out" title="Log out">
        <IconLogout size={18} />
      </button>
    </div>
  );
}

export default function SidebarLayout({ setLoggedIn, children }: Props) {
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const { setAuthenticated } = useContext(AuthContext);
  const { user } = useContext(UserContext);
  const [menuOpen, setMenuOpen] = useState(false);
  const userName = user?.userName || storedUserName();

  const logout = () => {
    localStorage.clear();
    setLoggedIn(false);
    setAuthenticated(false);
    navigate("/login");
  };

  // Each route starts at the top; the window is the scroller.
  useEffect(() => {
    window.scrollTo({ top: 0 });
    setMenuOpen(false);
  }, [pathname]);

  const inBottomNav = BOTTOM_NAV.some((item) => isPathActive(pathname, item.path));

  return (
    <div className="sq-shell">
      <a href="#sq-content" className="sq-skip">
        Skip to content
      </a>

      {/* Desktop */}
      <aside className="sq-sidebar" aria-label="Main navigation">
        <div className="sq-sidebar-brand">
          <img src={mark} alt="Speed Queen" />
          <span>Admin portal</span>
        </div>
        <nav className="sq-sidebar-nav">
          <NavList pathname={pathname} />
        </nav>
        <UserBlock name={userName} onLogout={logout} />
      </aside>

      {/* Phone and tablet */}
      <header className="sq-topbar">
        <img src={mark} alt="" className="sq-topbar-mark" />
        <span className="sq-topbar-title">{pageTitleFor(pathname)}</span>
      </header>

      <main className="sq-main" id="sq-content">
        <div className="sq-content">{children}</div>
        <Footer />
      </main>

      <nav className="sq-bottomnav" aria-label="Quick navigation">
        {BOTTOM_NAV.map((item) => (
          <NavLink key={item.path} to={item.path} className="sq-bottomnav-link">
            <item.icon size={22} stroke={1.75} />
            <span>{item.label}</span>
          </NavLink>
        ))}
        <button
          type="button"
          className={`sq-bottomnav-link${!inBottomNav ? " active" : ""}`}
          onClick={() => setMenuOpen(true)}
          aria-haspopup="dialog"
          aria-expanded={menuOpen}
        >
          <IconMenu2 size={22} stroke={1.75} />
          <span>Menu</span>
        </button>
      </nav>

      <Drawer
        anchor="bottom"
        open={menuOpen}
        onClose={() => setMenuOpen(false)}
        PaperProps={{ className: "sq-menu-sheet" }}
      >
        <div className="sq-menu-head">
          <span>Menu</span>
          <button type="button" onClick={() => setMenuOpen(false)} aria-label="Close menu">
            <IconX size={20} />
          </button>
        </div>
        <nav className="sq-menu-body" aria-label="All pages">
          <NavList pathname={pathname} onNavigate={() => setMenuOpen(false)} />
        </nav>
        <UserBlock name={userName} onLogout={logout} />
      </Drawer>
    </div>
  );
}
